import React, { useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Stack,
  Switch,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import LayersIcon from '@mui/icons-material/Layers'
import { msg, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { maplibreGroupChildButtonSx } from '../../consts/map-floating-control-sx'
import SvgCustomIcon from '../icons/SvgCustomIcon'

const PALETTE_GRADIENTS = {
  red: ['#DDB100', '#DD8500', '#DD5800', '#DD2C00', '#DD0000'],
  blue: ['#00A0CB', '#0079B8', '#0051A6', '#002A93', '#000280'],
  gray: ['#BBBEBF', '#959798', '#6F7171', '#494A4A', '#232323']
}

function getPaletteGradient(layer) {
  const colors = PALETTE_GRADIENTS[layer.palette]
  if (colors) return colors.join(',')
  const c = layer.color || '#6366F1'
  return `${c},${c}`
}

const LayerMapButton = React.memo(
  ({
    layersList,
    setLayersList,
    isFormOpen,
    isPositioning,
    isPositionSet,
    canReorderMapLayers = false,
    onMoveLayerUp,
    onMoveLayerDown,
    isMapLayerReordering = false
  }) => {
    const [anchorEl, setAnchorEl] = useState(null)
    const theme = useTheme()
    const { _ } = useLingui()

    const isLight = theme.palette.mode === 'light'
    const textPrimary = isLight
      ? theme.palette.newColors?.gray || theme.palette.text.primary
      : theme.palette.newColors?.lightGray || theme.palette.text.primary
    const textSecondary = theme.palette.text.secondary
    const surfaceBg = isLight ? theme.palette.background.paper : theme.palette.background.default
    const rowBorder = theme.palette.divider
    const rowHoverBg = isLight ? 'rgba(99, 102, 241, 0.04)' : 'rgba(255, 255, 255, 0.04)'
    const activeRowBg = isLight ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.16)'

    const handleButtonClick = (event) => {
      setAnchorEl(anchorEl ? null : event.currentTarget)
    }

    const open = Boolean(anchorEl)
    const id = open ? 'map-layer-popover' : undefined
    const reorderDisabled =
      isFormOpen || isPositioning || isPositionSet || isMapLayerReordering

    const reorderBtnSx = {
      width: 28,
      height: 28,
      border: `1px solid ${rowBorder}`,
      borderRadius: 'var(--radius-md, 6px)',
      color: textSecondary,
      '&:hover:not(.Mui-disabled)': {
        backgroundColor: rowHoverBg,
        color: theme.palette.primary.main
      },
      '&.Mui-disabled': {
        opacity: 0.35
      }
    }

    return (
      <>
        <Button
          type="button"
          className="fred-map-toolbar-btn"
          aria-describedby={id}
          onClick={handleButtonClick}
          sx={maplibreGroupChildButtonSx(theme)}
        >
          <SvgCustomIcon color="#425B61" name="stacks" size={24} />
        </Button>

        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          slotProps={{
            paper: {
              sx: {
                borderRadius: 'var(--radius-2xl, 12px)',
                boxShadow: theme.shadows[8],
                overflow: 'hidden',
                minWidth: 320,
                maxWidth: 400
              }
            }
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              bgcolor: surfaceBg,
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 'min(420px, 70vh)'
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: `1px solid ${rowBorder}`,
                bgcolor: isLight ? 'var(--color-background, #F8FAFC)' : 'transparent'
              }}
            >
              <LayersIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
              <Typography
                sx={{
                  flex: 1,
                  fontSize: 'var(--font-lg, 14px)',
                  fontWeight: 700,
                  color: textPrimary
                }}
              >
                <Trans>Additional Data</Trans>
              </Typography>
              {isMapLayerReordering && (
                <CircularProgress size={18} thickness={5} />
              )}
            </Stack>

            <Box sx={{ overflowY: 'auto', px: 1.5, py: 1.5, flex: 1 }}>
              {layersList.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ px: 1, py: 2, textAlign: 'center', color: textSecondary }}
                >
                  <Trans>No map layers available</Trans>
                </Typography>
              ) : (
                <Stack spacing={1} divider={<Divider flexItem />}>
                  {layersList.map((layer, i) => {
                    const isFirst = i === 0
                    const isLast = i === layersList.length - 1
                    const formatLabel = layer.fileExtension || 'geojson'
                    const title = layer.title || layer.description || _(msg`Untitled layer`)

                    return (
                      <Box
                        key={layer.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'stretch',
                          gap: 1,
                          p: 1.25,
                          borderRadius: 'var(--radius-lg, 8px)',
                          border: `1px solid ${layer.isOpen ? theme.palette.primary.main : rowBorder}`,
                          bgcolor: layer.isOpen ? activeRowBg : 'transparent',
                          transition: 'background-color 0.15s ease, border-color 0.15s ease',
                          '&:hover': {
                            bgcolor: layer.isOpen ? activeRowBg : rowHoverBg
                          }
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack
                            direction="row"
                            alignItems="flex-start"
                            spacing={0.75}
                            sx={{ mb: 0.75 }}
                          >
                            <Typography
                              variant="body2"
                              title={title}
                              sx={{
                                flex: 1,
                                fontWeight: 600,
                                fontSize: 'var(--font-md, 13px)',
                                color: textPrimary,
                                lineHeight: 1.35,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                              }}
                            >
                              {title}
                            </Typography>
                            <Chip
                              label={formatLabel}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 20,
                                fontSize: 'var(--font-xs, 10px)',
                                fontWeight: 600,
                                flexShrink: 0,
                                borderColor: rowBorder,
                                color: textSecondary
                              }}
                            />
                          </Stack>
                          <Box
                            aria-hidden
                            sx={{
                              height: 3,
                              width: '100%',
                              borderRadius: 'var(--radius-full, 9999px)',
                              backgroundImage: `linear-gradient(to right, ${getPaletteGradient(layer)})`
                            }}
                          />
                          {layer.isLoading && (
                            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.75 }}>
                              <CircularProgress size={12} thickness={5} />
                              <Typography variant="caption" color="text.secondary">
                                <Trans>Loading…</Trans>
                              </Typography>
                            </Stack>
                          )}
                        </Box>

                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                          sx={{ flexShrink: 0, alignSelf: 'center' }}
                        >
                          {canReorderMapLayers && (
                            <Stack direction="column" spacing={0.25}>
                              <Tooltip title={_(msg`Move layer up`)} placement="left">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={reorderDisabled || isFirst}
                                    aria-label={_(msg`Move layer up`)}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      onMoveLayerUp?.(layer.id)
                                    }}
                                    sx={reorderBtnSx}
                                  >
                                    <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={_(msg`Move layer down`)} placement="left">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={reorderDisabled || isLast}
                                    aria-label={_(msg`Move layer down`)}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      onMoveLayerDown?.(layer.id)
                                    }}
                                    sx={reorderBtnSx}
                                  >
                                    <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          )}
                          <Switch
                            size="small"
                            checked={Boolean(layer.isOpen)}
                            disabled={layer.isLoading}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              const checked = e.target.checked
                              setLayersList(
                                layersList.map((item) => {
                                  if (item.id !== layer.id) {
                                    return { ...item, isFocused: false }
                                  }
                                  if (checked) {
                                    return {
                                      ...item,
                                      isOpen: true,
                                      isFocused: true
                                    }
                                  }
                                  return {
                                    ...item,
                                    isOpen: false,
                                    isFocused: false,
                                    isLoading: false,
                                    fileContent: null,
                                    isLoaded: false
                                  }
                                })
                              )
                            }}
                            sx={{
                              ml: 0.25,
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: theme.palette.primary.main
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: theme.palette.primary.main
                              }
                            }}
                          />
                        </Stack>
                      </Box>
                    )
                  })}
                </Stack>
              )}
            </Box>

            {canReorderMapLayers && layersList.length > 1 && (
              <Typography
                variant="caption"
                sx={{
                  px: 2,
                  py: 1,
                  borderTop: `1px solid ${rowBorder}`,
                  color: textSecondary,
                  bgcolor: isLight ? 'var(--color-background, #F8FAFC)' : 'transparent'
                }}
              >
                <Trans>Top of the list is drawn above on the map.</Trans>
              </Typography>
            )}
          </Box>
        </Popover>
      </>
    )
  }
)
export default LayerMapButton
